<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import useCheckout from '../composables/useCheckout';
import {i18n} from "@/libs/wordpress.facade";

const {step, wooCart} = useCheckout();

function printCurrency(value: string|number, currency: string){
	return (Number(value)/100).toLocaleString(undefined, {currency: currency, style: 'currency'});
}

const products = computed(() => {
	if(!wooCart.cart.value) return [];
	return wooCart.cart.value.items.map(item => {

		const subItemMeta = [];
		
		if(item.extensions.itlp.fields.confezione_regalo && item.extensions.itlp.fields.confezione_regalo.requested){
			const price = Number(item.extensions.itlp.fields.confezione_regalo.prezzo);
			subItemMeta.push({
				label: i18n.__('Confezione regalo', 'italpreziosi'),
				description: item.extensions.itlp.fields.confezione_regalo.descrizione,
				price: isNaN(price) ? '' : price.toLocaleString(undefined, {style: 'currency', currency: item.totals.currency_code})
			});
		}

		if(item.extensions.itlp.fields.personalizzazione && item.extensions.itlp.fields.personalizzazione.requested){
			const price = Number(item.extensions.itlp.fields.personalizzazione.prezzo);
			subItemMeta.push({
				label: i18n.__('Personalizzazione', 'italpreziosi'),
				descriptions: item.extensions.itlp.fields.personalizzazione.descrizione,
				price: isNaN(price) ? '' : price.toLocaleString(undefined, {style: 'currency', currency: item.totals.currency_code})
			});
		}

		let name = item.name;
		if(item.type == 'variation' && item.variation){
			for (const variation of item.variation) {
				name += ' - ' + variation.value;
			}
		}

		let totalWithTax = Number(item.totals.line_subtotal) + Number(item.totals.line_subtotal_tax);

		const totalPriceText = printCurrency(totalWithTax, item.totals.currency_code);

		return {
			image: item.images ? item.images[0] : undefined,
			name,
			totals: item.totals,
			totalPriceText,
			subItems: subItemMeta,
			quantity: item.quantity
		};
	});
});

const cart = toRef(wooCart.cart);

const showAddress = computed(() => {
	if(wooCart.hasStonexShipping.value) return step.value >= 3;
	if(!wooCart.selectedShippingRate.value) return false;
	return !["italdeposito_shipping", 'italpreziosi_local_pickup'].includes(wooCart.selectedShippingRate.value.method_id) && step.value >= 3;
});

const showReceiveProduct = computed(() => {
	if(wooCart.hasStonexShipping.value) return true;
	if(!wooCart.selectedShippingRate.value) return false;
	return !["italdeposito_shipping"].includes(wooCart.selectedShippingRate.value.method_id);
});

const showShippingType = computed(() => {
	if(!wooCart.selectedShippingRate.value) return false;
	return step.value >= 2 || !showReceiveProduct.value; 
});

const coupons = computed(() => {
	if(!wooCart.cart.value?.coupons || !wooCart.cart.value?.coupons.length) return [];
	const couponsData = [];

	for (const coupon of wooCart.cart.value?.coupons){
		let amount_text = '-'+printCurrency(coupon.totals.total_discount, coupon.totals.currency_code);
		if(coupon.discount_type == 'percent' && wooCart.cart.value.extensions.itlp?.coupons[coupon.code]){
			amount_text = '-'+wooCart.cart.value.extensions.itlp.coupons[coupon.code].amount+'%'
		}
		couponsData.push({
			name: coupon.code,
			amount_text
		});
	}

	return couponsData;
});

const couponValue = ref('');

function addCoupon(){
	wooCart.applyCoupon({
		code: couponValue.value
	})
	.then(() => {
		couponValue.value = '';
	})
	.catch(e => {

	});
}

function removeCoupon(coupon: {name: string}){
	wooCart.removeCoupon({
		code: coupon.name
	})
	.catch(e => {

	});
}

const shipingCostAmountText = computed(() => {
	if(!cart.value) return '';
	const totalShippingAmount = Number(cart.value.totals.total_shipping);
	if(isNaN(totalShippingAmount)) return '';
	if(!totalShippingAmount) return i18n.__('Gratuito', 'italpreziosi');
	return (Number(cart.value.totals.total_shipping)/100).toLocaleString(undefined, {currency: cart.value.totals.currency_code, style: 'currency'});
});

const shipingCostItlpAmountText = computed(() => {
	if(!wooCart.selectedShippingRate.value) return '';
	const itlpShippingAmount = Number(wooCart.selectedShippingRate.value.price);
	if(isNaN(itlpShippingAmount)) return '';
	if(!itlpShippingAmount) return i18n.__('Gratuito', 'italpreziosi');
	return (Number(itlpShippingAmount)/100).toLocaleString(undefined, {currency: wooCart.selectedShippingRate.value.currency_code, style: 'currency'});
});

const shipingCostStonexAmountText = computed(() => {
	if(!wooCart.selectedStonexShippingRate.value) return '';
	const stonexShippingAmount = Number(wooCart.selectedStonexShippingRate.value.price);
	if(isNaN(stonexShippingAmount)) return '';
	if(!stonexShippingAmount) return i18n.__('Gratuito', 'italpreziosi');
	return (Number(stonexShippingAmount)/100).toLocaleString(undefined, {currency: wooCart.selectedStonexShippingRate.value.currency_code, style: 'currency'});
});

const totalTexts = computed(() => {
	const tots = { 
		totalTax: '',
		totalPrice: ''
	};

	if(cart.value && cart.value.totals){
		const totalTax = Number(cart.value.totals.total_tax);
		if(totalTax) tots.totalTax = (totalTax/100).toLocaleString(undefined, {currency: cart.value.totals.currency_code, style: 'currency'});
		tots.totalPrice = (Number(cart.value.totals.total_price)/100).toLocaleString(undefined, {currency: cart.value.totals.currency_code, style: 'currency'});
	}

	return tots;
});

</script>

<template lang="pug">
.itlp-checkout-summary
	.itlp-checkout-summary-products
		.itlp-checkout-summary-products-title {{i18n.__('Prodotti', 'italpreziosi')}}
		.itlp-checkout-summary-products-list
			.itlp-checkout-summary-products-item(v-for='item in products')
				img(:src="item.image?.thumbnail" :alt="item.image?.alt")
				div.itlp-checkout-summary-products-item-row-wrap
					.itlp-checkout-summary-products-item-row
						.itlp-checkout-summary-products-item-name 
							span {{ item.name }}
							span.itlp-checkout-summary-products-item-qty {{ item.quantity }}
						.itlp-checkout-summary-products-item-price {{ item.totalPriceText }}
					.itlp-checkout-summary-products-item-sub-row(v-for='subItem in item.subItems')
						.itlp-checkout-summary-products-item-fake-checkbox
						.itlp-checkout-summary-products-item-name {{ subItem.label }}
						.itlp-checkout-summary-products-item-price {{ subItem.price }}
	.itlp-checkout-summary-discount(v-if='cart')
		.itlp-checkout-summary-discount-title 
			label(for="discount-code-input") {{i18n.__('Codice sconto', 'italpreziosi')}}
			span(v-if='cart.totals.total_discount && cart.totals.total_discount != "0"') {{ '-'+printCurrency(cart.totals.total_discount, cart.totals.currency_code) }}
		.itlp-checkout-summary-discount-input-wrap
			input#discount-code-input.itlp-checkout-summary-discount-input-text(type="text" v-model="couponValue" name="coupon_code" placeholder="AA-0000-ZZ")
			button(:value="i18n.__('Applica codice promozionale', 'italpreziosi')" @click='addCoupon()')
				span {{i18n.__('Applica', 'italpreziosi')}}				
				img(decoding="async" src="https://shop.italpreziosi.it/wp-content/uploads/2025/05/right-arrow.svg")
		.itlp-checkout-summary-discount-coupon(v-for='coupon in coupons')
			span {{ coupon.name }}
			span.itlp-checkout-summary-discount-coupon-remove(@click="removeCoupon(coupon)") {{ i18n.__('Rimuovi', 'italpreziosi') }}
			span {{ coupon.amount_text }}
	.itlp-checkout-summary-shipping(v-if='showAddress || showShippingType || showReceiveProduct')
		.itlp-checkout-summary-shipping-title {{i18n.__('Modalità di gestione', 'italpreziosi')}}
		.itlp-checkout-summary-shipping-method
			.itlp-checkout-summary-shipping-method-row.itlp-checkout-summary-shipping-method-receive(v-if='showReceiveProduct')
				.itlp-checkout-summary-shipping-method-radio-btn
				.itlp-checkout-summary-shipping-method-name {{i18n.__('Ricevi il Prodotto', 'italpreziosi')}}
				img.itlp-checkout-summary-shipping-method-icon(decoding="async" src="https://shop.italpreziosi.it/wp-content/uploads/2025/04/icona-spedizione-portavalori.svg" alt="Spedizione assicurata con corriere portavalori")
			.itlp-checkout-summary-shipping-method-row(v-if="showShippingType && wooCart.selectedShippingRate.value")
				.itlp-checkout-summary-shipping-method-radio-btn
				.itlp-checkout-summary-shipping-method-name {{wooCart.selectedShippingRate.value.name}}
				.itlp-checkout-summary-shipping-method-icon
		.itlp-checkout-summary-shipping-address(v-if='showAddress && cart')
			.itlp-checkout-summary-shipping-address-title {{i18n.__('Destinatario', 'italpreziosi')}}
			.itlp-checkout-summary-shipping-address-content
				span {{ cart.shipping_address.first_name }} {{ cart.shipping_address.last_name }} 
				span - {{ cart.shipping_address.address_1 }} 
				span - {{ cart.shipping_address.city }}
				span , {{ cart.shipping_address.state }}
				span {{ cart.shipping_address.postcode }}
				span {{ cart.shipping_address.country }}
		.itlp-checkout-summary-shipping-cost(v-if='shipingCostItlpAmountText')
			.itlp-checkout-summary-shipping-cost-title {{i18n.__('Spedizione Italpreziosi', 'italpreziosi')}}
			.itlp-checkout-summary-shipping-cost-amount {{ shipingCostItlpAmountText }}
		.itlp-checkout-summary-shipping-cost(v-if='shipingCostStonexAmountText')
			.itlp-checkout-summary-shipping-cost-title {{i18n.__('Spedizione dal nostro deposito estero', 'italpreziosi')}}
			.itlp-checkout-summary-shipping-cost-amount {{ shipingCostStonexAmountText }}
	.itlp-checkout-summary-fees(v-if='cart && cart.fees')
		.itlp-checkout-summary-fee-row(v-for='fee in cart.fees')
			.itlp-checkout-summary-fee-name {{fee.name}}:
			.itlp-checkout-summary-fee-price {{ (Number(fee.totals.total)/100).toLocaleString(undefined, {currency: fee.totals.currency_code, style: 'currency'}) }}
	.itlp-checkout-summary-taxes(v-if='totalTexts.totalTax')
		.itlp-checkout-summary-taxes-row
			.itlp-checkout-summary-taxes-name {{i18n.__('IVA', 'italpreziosi')}}:
			.itlp-checkout-summary-taxes-price {{ totalTexts.totalTax }}
	.itlp-checkout-summary-totals(v-if='cart')
		.itlp-checkout-summary-totals-row 
			.itlp-checkout-summary-totals-name {{i18n.__('Totale Ordine', 'italpreziosi')}}:
			.itlp-checkout-summary-totals-price {{ totalTexts.totalPrice }}
</template>